import React, { useState } from 'react';
import { Upload } from 'antd';
import InputMask from 'react-input-mask';
import { PlusOutlined, LoadingOutlined } from '@ant-design/icons';
import {
  MapPinIcon,
  PhoneIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import AddressAutocomplete from '../../../../components/address-autocomplete/address-autocomplete';
import storeApi from '../../../../services/storeApi';
import { apartmentValue } from '../../../../utils/apartment';
import DialogShell from '../components/dialog-shell';
import {
  SectionHeading,
  DetailRow,
  FieldLabel,
  FieldError,
  inputBaseClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../components/settings-ui';

// Apt / Suite is cleaned once, as the form loads — never per render: trimming
// the typed value each keystroke ate its spaces ("Suite 200" saved "Suite200",
// owner report 2026-09-25).
const toForm = (storeDetail) => ({
  ...storeDetail,
  apartment: apartmentValue(storeDetail.apartment),
});

const StoreTab = ({ storeDetail, checkUpdate }) => {
  const [form, setForm] = useState(() => toForm(storeDetail));
  const [errors, setErrors] = useState({
    name: false,
    responsiblename: false,
    responsiblephone: false,
    address: false,
    phone: false,
  });
  const [loading, setLoading] = useState(false);
  const [dialogs, setDialogs] = useState({ upsert: false });

  const onFormChange = (val, key) => {
    if (key === 'name') {
      // Allow punctuation common in business names (e.g. "Sam's Kebob & Gyro's - Mediterranean Grill").
      const numAndLetters = /^[A-Za-z0-9 _'’&.,()/-]*$/;
      setForm((prev) => ({
        ...prev,
        [key]: numAndLetters.test(val) ? val : prev[key],
      }));
    } else {
      setForm((prev) => ({ ...prev, [key]: val }));
    }
  };

  const onClickEdit = () => {
    setForm(toForm(storeDetail));
    setDialogs((prev) => ({ ...prev, upsert: true }));
  };

  const onFormSubmit = async () => {
    if (
      !form.name ||
      !form.responsiblename ||
      !form.responsiblephone ||
      !form.address ||
      !form.phone
    ) {
      setErrors({
        name: !form.name,
        responsiblename: !form.responsiblename,
        responsiblephone: !form.responsiblephone,
        address: !form.address,
        phone: !form.phone,
      });
      return;
    }
    setErrors({
      responsiblephone: false,
      name: false,
      responsiblename: false,
      address: false,
      phone: false,
    });

    try {
      setLoading(true);
      const body = { ...form, websiteclosedat: form.websiteclosedat || '' };
      const config = { headers: { 'Content-Type': 'multipart/form-data' } };
      const res = await storeApi.upsert(body, config);
      if (res.status === 200) {
        checkUpdate(true);
      }
    } catch (error) {
      checkUpdate(false, error?.response?.data);
    } finally {
      setLoading(false);
      setDialogs((prev) => ({ ...prev, upsert: false }));
    }
  };

  const onAddressSelect = (obj) => {
    if (typeof obj === 'object') {
      setForm((prev) => ({
        ...prev,
        address: obj.street,
        city: obj.city,
        state: obj.state,
        postalcode: obj.zipCode,
        longitude: obj?.longitude || '',
        latitude: obj?.latitude || '',
        apartment: obj?.apartment || '',
      }));
    } else {
      setForm((prev) => ({ ...prev, address: obj }));
    }
  };

  const onCloseUpsert = () => {
    setErrors({
      address: false,
      name: false,
      responsiblename: false,
      responsiblephone: false,
      phone: false,
    });
    setDialogs((prev) => ({ ...prev, upsert: false }));
  };

  const uploadButton = (
    <div className="flex flex-col items-center justify-center text-slate-500 dark:text-slate-400">
      {loading ? (
        <LoadingOutlined className="text-lg" />
      ) : (
        <PlusOutlined className="text-lg" />
      )}
      <span className="mt-2 text-xs font-medium">Upload</span>
    </div>
  );

  const fullAddress = [
    storeDetail.address,
    storeDetail.apartment && storeDetail.apartment !== 'null'
      ? storeDetail.apartment
      : null,
  ]
    .filter(Boolean)
    .join(', ');

  const cityLine = [storeDetail.city, storeDetail.state, storeDetail.postalcode]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="space-y-6">
      <SectionHeading
        title="Store Information"
        description="Customer-facing details and the manager responsible for this location."
        action={
          <button
            type="button"
            onClick={onClickEdit}
            className={primaryButtonClass}
          >
            Edit details
          </button>
        }
      />

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-md border border-slate-200 bg-gradient-to-br from-white to-slate-50/70 p-5 shadow-sm dark:border-slate-700 dark:from-slate-900/60 dark:to-slate-900/20">
          <div className="flex items-start gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              <MapPinIcon className="h-6 w-6" aria-hidden="true" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                Address
              </p>
              <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100">
                {fullAddress || '—'}
              </p>
              {cityLine && (
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  {cityLine}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-md border border-slate-200 bg-gradient-to-br from-white to-slate-50/70 p-5 shadow-sm dark:border-slate-700 dark:from-slate-900/60 dark:to-slate-900/20">
          <div className="flex items-start gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              <PhoneIcon className="h-6 w-6" aria-hidden="true" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                Store phone
              </p>
              <p className="mt-1 text-sm font-semibold tabular-nums text-slate-800 dark:text-slate-100">
                {storeDetail.phone || '—'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-md border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900/60">
        <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
          Details
        </h3>
        <dl className="mt-3">
          <DetailRow label="Store name">{storeDetail.name}</DetailRow>
          <DetailRow label="Store phone">{storeDetail.phone}</DetailRow>
          <DetailRow label="Manager name">
            <span className="inline-flex items-center gap-2">
              <UserCircleIcon
                className="h-5 w-5 text-slate-400 dark:text-slate-500"
                aria-hidden="true"
              />
              {storeDetail.responsiblename}
            </span>
          </DetailRow>
          <DetailRow label="Manager phone">
            <span className="tabular-nums">{storeDetail.responsiblephone}</span>
          </DetailRow>
        </dl>
      </div>

      <DialogShell
        show={dialogs.upsert}
        onClose={onCloseUpsert}
        title="Edit store details"
        description="Update the customer-facing information for this location."
        size="lg"
        footer={
          <>
            <button
              type="button"
              onClick={onCloseUpsert}
              disabled={loading}
              className={secondaryButtonClass}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onFormSubmit}
              disabled={loading}
              className={primaryButtonClass}
            >
              {loading ? 'Saving…' : 'Save changes'}
            </button>
          </>
        }
      >
        <div className="space-y-6">
          <div className="space-y-2">
            <FieldLabel>Store logo</FieldLabel>
            <Upload
              multiple={false}
              maxCount={1}
              name="avatar"
              onChange={(e) => onFormChange(e.file, 'storelogo')}
              listType="picture-circle"
              className="avatar-uploader"
              beforeUpload={() => false}
            >
              {storeDetail.storelogo ? (
                <img
                  src={storeDetail.storelogo}
                  alt="avatar"
                  style={{ width: '100%', height: '100%' }}
                  className="rounded-full object-cover"
                />
              ) : (
                uploadButton
              )}
            </Upload>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <FieldLabel htmlFor="name" required>
                Store name
              </FieldLabel>
              <input
                id="name"
                type="text"
                value={form.name || ''}
                onChange={(e) => onFormChange(e.target.value, 'name')}
                placeholder="e.g. ABC Pizza"
                className={inputBaseClass}
              />
              <FieldError
                message={errors.name ? 'Store name is required.' : null}
              />
            </div>
            <div className="space-y-2">
              <FieldLabel htmlFor="phone" required>
                Store phone
              </FieldLabel>
              <InputMask
                id="phone"
                type="text"
                name="phone"
                value={form.phone || ''}
                mask="(999) 999 9999"
                maskPlaceholder={' '}
                onChange={(e) => onFormChange(e.target.value, 'phone')}
                className={inputBaseClass + ' tabular-nums'}
                placeholder="(555) 123 4567"
              />
              <FieldError
                message={errors.phone ? 'Phone number is required.' : null}
              />
            </div>
          </div>

          <div className="space-y-4 rounded-md border border-slate-200 bg-slate-50/50 p-4 dark:border-slate-700 dark:bg-slate-900/30">
            <div>
              <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                Store address
              </h4>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                This will be displayed publicly on your customer-facing pages.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-4">
              <div className="space-y-2 sm:col-span-3">
                <FieldLabel htmlFor="address">Street</FieldLabel>
                <AddressAutocomplete
                  value={form.address}
                  onChange={(e) => onAddressSelect(e)}
                />
                <FieldError
                  message={errors.address ? 'Address is required.' : null}
                />
              </div>
              <div className="space-y-2">
                <FieldLabel htmlFor="apartment">Apt / Suite</FieldLabel>
                <input
                  id="apartment"
                  type="text"
                  value={form.apartment || ''}
                  onChange={(e) => onFormChange(e.target.value, 'apartment')}
                  className={inputBaseClass}
                />
                <FieldError />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-4">
              <div className="space-y-2 sm:col-span-2">
                <FieldLabel htmlFor="city">City</FieldLabel>
                <input
                  id="city"
                  type="text"
                  value={form.city || ''}
                  onChange={(e) => onFormChange(e.target.value, 'city')}
                  className={inputBaseClass}
                />
              </div>
              <div className="space-y-2">
                <FieldLabel htmlFor="state">State</FieldLabel>
                <input
                  id="state"
                  type="text"
                  value={form.state || ''}
                  onChange={(e) => onFormChange(e.target.value, 'state')}
                  className={inputBaseClass}
                />
              </div>
              <div className="space-y-2">
                <FieldLabel htmlFor="postalcode">Zip</FieldLabel>
                <input
                  id="postalcode"
                  type="text"
                  value={form.postalcode || ''}
                  onChange={(e) => onFormChange(e.target.value, 'postalcode')}
                  className={inputBaseClass + ' tabular-nums'}
                />
              </div>
            </div>
          </div>

          <div className="space-y-4 rounded-md border border-slate-200 bg-slate-50/50 p-4 dark:border-slate-700 dark:bg-slate-900/30">
            <div>
              <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                Store manager
              </h4>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                This information <strong>will not</strong> be displayed
                publicly.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <FieldLabel htmlFor="responsiblename" required>
                  Manager's full name
                </FieldLabel>
                <input
                  id="responsiblename"
                  type="text"
                  value={form.responsiblename || ''}
                  onChange={(e) =>
                    onFormChange(e.target.value, 'responsiblename')
                  }
                  placeholder="e.g. John Doe"
                  className={inputBaseClass}
                />
                <FieldError
                  message={
                    errors.responsiblename
                      ? "Manager's name is required."
                      : null
                  }
                />
              </div>
              <div className="space-y-2">
                <FieldLabel htmlFor="responsiblephone" required>
                  Manager's phone
                </FieldLabel>
                <InputMask
                  id="responsiblephone"
                  type="text"
                  name="responsiblephone"
                  value={form.responsiblephone || ''}
                  mask="(999) 999 9999"
                  maskPlaceholder={' '}
                  onChange={(e) =>
                    onFormChange(e.target.value, 'responsiblephone')
                  }
                  className={inputBaseClass + ' tabular-nums'}
                  placeholder="(555) 123 4567"
                />
                <FieldError
                  message={
                    errors.responsiblephone
                      ? "Manager's phone is required."
                      : null
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </DialogShell>
    </div>
  );
};

export default StoreTab;
